import axios, { AxiosError, type AxiosRequestConfig } from "axios";
import type { EnquiryPayload, ApplicationPayload, FormResponse, Partner, BlogPost, Job, Faq } from "../types";

/* Self-hosted builds (www.aviokan.com) set REACT_APP_BACKEND_URL to nothing →
   every API call becomes a relative /api/... path served same-origin by nginx. */
export const BACKEND = process.env.REACT_APP_BACKEND_URL || "";
export const API = `${BACKEND}/api`;

export const api = axios.create({ baseURL: API, timeout: 15000 });

export const fetchPartners = (params?: Record<string, string | number>): Promise<{ partners: Partner[]; count: number }> =>
  api.get("/partners", { params }).then((r) => r.data);
export const fetchBlog = (params?: Record<string, string | number>): Promise<{ posts: BlogPost[]; count: number }> =>
  api.get("/blog", { params }).then((r) => r.data);
export const fetchJobs = (): Promise<{ jobs: Job[] }> => api.get("/jobs").then((r) => r.data);
export const searchAll = (q: string): Promise<{ groups: { label: string; to: string; sub?: string }[] }> =>
  api.get("/search", { params: { q } }).then((r) => r.data);
export const submitEnquiry = (payload: EnquiryPayload): Promise<FormResponse> =>
  api.post("/enquiries", payload).then((r) => r.data);
export const submitApplication = (payload: ApplicationPayload): Promise<FormResponse> =>
  api.post("/applications", payload).then((r) => r.data);
export const fetchFaqs = (scope?: string): Promise<{ faqs: Faq[] }> =>
  api.get("/faqs", { params: { scope } }).then((r) => r.data);

/* ---------- Admin (JWT bearer) ---------- */

interface AdminTokenPair {
  access_token: string;
  refresh_token: string;
}

export const adminApi = axios.create({ baseURL: API });

adminApi.interceptors.request.use((cfg) => {
  const t = localStorage.getItem("ak_admin_access");
  if (t) cfg.headers.Authorization = `Bearer ${t}`;
  return cfg;
});

let refreshing: Promise<{ data: AdminTokenPair }> | null = null;

interface RetriableConfig extends AxiosRequestConfig {
  _retried?: boolean;
  headers?: Record<string, string>;
}

adminApi.interceptors.response.use(
  (r) => r,
  async (error: AxiosError) => {
    const orig = error.config as RetriableConfig;
    if (error.response?.status === 401 && orig && !orig._retried) {
      orig._retried = true;
      const rt = localStorage.getItem("ak_admin_refresh");
      if (rt) {
        try {
          refreshing = refreshing || axios.post(`${API}/admin/refresh`, { refresh_token: rt });
          const { data } = await refreshing;
          refreshing = null;
          localStorage.setItem("ak_admin_access", data.access_token);
          (orig.headers = orig.headers || {}).Authorization = `Bearer ${data.access_token}`;
          return adminApi(orig);
        } catch {
          refreshing = null;
        }
      }
      localStorage.removeItem("ak_admin_access");
      localStorage.removeItem("ak_admin_refresh");
      window.dispatchEvent(new Event("ak-admin-logout"));
    }
    return Promise.reject(error);
  }
);

export interface ApiErrorDetail {
  detail?: unknown;
  message?: string;
}

export const formatApiError = (e: unknown): string => {
  const err = e as AxiosError<ApiErrorDetail>;
  const detail = err?.response?.data?.detail;
  if (detail == null) return err?.message || "Something went wrong. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail
      .map((x) => (typeof x === "object" && x !== null && "msg" in x ? String((x as { msg: unknown }).msg) : JSON.stringify(x)))
      .filter(Boolean)
      .join(", ");
  if (typeof detail === "object" && detail !== null && "msg" in detail) return String((detail as { msg: unknown }).msg);
  return String(detail);
};

export const adminLogin = (email: string, password: string): Promise<AdminTokenPair> =>
  axios.post(`${API}/admin/login`, { email, password }).then((r) => {
    localStorage.setItem("ak_admin_access", r.data.access_token);
    localStorage.setItem("ak_admin_refresh", r.data.refresh_token);
    return r.data;
  });

export const adminLogout = () => {
  localStorage.removeItem("ak_admin_access");
  localStorage.removeItem("ak_admin_refresh");
};

export const fetchCaseStudies = async () => {
  const r = await fetch(`${API}/case-studies`);
  if (!r.ok) throw new Error("case studies unavailable");
  const d = await r.json();
  return d.case_studies || [];
};
